import { DISPOSAL_PURPOSE, FREIGHT_TYPE, SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { CurrencyCodeSchema } from '@schuettflix/utils';
import { z } from 'zod';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';
import { ProcessedBySchema } from '@/shared/models/ContextDataModel/interfaces/enums/processed-by.ts';
import { VehicleClass } from '@/shared/models/ContextDataModel/interfaces/entities/vehicle-class.ts';
import { LocationConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/location-configuration.ts';
import { LocationType } from '@/modules/location/domain/LocationSchema.ts';

const AutomaticBillingUnitSchema = SUPPORTED_UNITS_SCHEMA.extract(['TRANSPORT', 'TON']);

function parseCarrierOrganizationId(productConfiguration: ProductConfiguration): number | 'TRANSPORT_EXCHANGE' {
    if (!('processedBy' in productConfiguration.additionalProperties)) {
        throw new Error('No processedBy field found in product configuration additional properties');
    }

    if (productConfiguration.additionalProperties.processedBy === ProcessedBySchema.Enum.TRANSPORT_EXCHANGE) {
        return 'TRANSPORT_EXCHANGE';
    }

    if (!productConfiguration.partnerOrganizationId) {
        throw new Error('No partner organization ID found in product configuration');
    }

    return productConfiguration.partnerOrganizationId;
}

export type SellerProductReference = {
    sellerProductId: string;
    productDefinitionId: string;
    legacyFactoryId?: number;
    name: string;
};

/**
 * Reconstructs the `sellerProduct` form field from a persisted material product configuration.
 * The seller product ids are stored on the material additional properties; the factory id is
 * derived from the loading location when available. The factory id is not persisted on the
 * backend, so on load it may be absent here. Returns undefined only when the seller product ids
 * themselves are missing.
 */
export function parseSellerProduct(
    materialProductConfiguration: ProductConfiguration,
    loadingLocation: LocationConfiguration | undefined
): SellerProductReference | undefined {
    const { additionalProperties } = materialProductConfiguration;

    if (
        !('sellerProductId' in additionalProperties) ||
        !additionalProperties.sellerProductId ||
        !('productDefinitionId' in additionalProperties) ||
        !additionalProperties.productDefinitionId
    ) {
        return undefined;
    }

    return {
        sellerProductId: additionalProperties.sellerProductId,
        productDefinitionId: additionalProperties.productDefinitionId,
        legacyFactoryId: loadingLocation?.location.legacyFactoryId ?? undefined,
        name: materialProductConfiguration.productName,
    };
}

export function parseTransportConfiguration(productConfiguration: ProductConfiguration): {
    transportProductConfiguration: Omit<ProductConfiguration, 'additionalProperties' | 'unit' | 'currencyCode'> & {
        unit: z.infer<typeof AutomaticBillingUnitSchema>;
        currencyCode: z.infer<typeof CurrencyCodeSchema>;
        salesQuantity?: number;
        salesUnit?: z.infer<typeof AutomaticBillingUnitSchema>;
        purchaseQuantity?: number;
        purchaseUnit?: z.infer<typeof AutomaticBillingUnitSchema>;
        additionalProperties: {
            freightType: FREIGHT_TYPE;
            vehicleClass: VehicleClass;
            disposalPurpose?: DISPOSAL_PURPOSE;
            disposalCertificateNumber?: string;
            loadingLocation?: LocationConfiguration;
            unloadingLocation?: LocationConfiguration;
            weighingLocation?: LocationType | null;
        };
    };
    carrierOrganizationId: number | 'TRANSPORT_EXCHANGE';
} {
    let freightType: FREIGHT_TYPE | undefined;
    let vehicleClass: VehicleClass | undefined;
    let disposalPurpose: DISPOSAL_PURPOSE | undefined;
    let disposalCertificateNumber: string | undefined;
    let unloadingLocation: LocationConfiguration | undefined;
    let loadingLocation: LocationConfiguration | undefined;
    let weighingLocation: LocationType | null | undefined;

    if ('freightType' in productConfiguration.additionalProperties) {
        freightType = productConfiguration.additionalProperties.freightType;
    }
    if ('vehicleClass' in productConfiguration.additionalProperties) {
        vehicleClass = {
            ...productConfiguration.additionalProperties.vehicleClass,
            payloadInTons: productConfiguration.additionalProperties.vehicleClass.payload / 1000,
        };
    }
    if ('locations' in productConfiguration.additionalProperties) {
        unloadingLocation = productConfiguration.additionalProperties.locations?.find(
            location => location.type === 'UNLOADING'
        );
        loadingLocation = productConfiguration.additionalProperties.locations?.find(
            location => location.type === 'LOADING'
        );
    }
    if ('weighingLocation' in productConfiguration.additionalProperties) {
        weighingLocation = productConfiguration.additionalProperties.weighingLocation;
    }

    if (!freightType || !vehicleClass) {
        console.error('Missing freightType or vehicleClass:', {
            freightType,
            vehicleClass,
        });
        throw new Error('Missing freightType or vehicleClass in product configuration additional properties');
    }

    if ('disposalPurpose' in productConfiguration.additionalProperties) {
        disposalPurpose = productConfiguration.additionalProperties.disposalPurpose;
    }
    if ('disposalCertificateNumber' in productConfiguration.additionalProperties) {
        disposalCertificateNumber = productConfiguration.additionalProperties.disposalCertificateNumber;
    }

    const carrierOrganizationId = parseCarrierOrganizationId(productConfiguration);

    return {
        transportProductConfiguration: {
            ...productConfiguration,
            unit: AutomaticBillingUnitSchema.parse(productConfiguration.unit),
            currencyCode: CurrencyCodeSchema.parse(productConfiguration.currencyCode),
            salesQuantity: productConfiguration.salesQuantity,
            salesUnit: AutomaticBillingUnitSchema.parse(productConfiguration.salesUnit),
            purchaseQuantity: productConfiguration.purchaseQuantity,
            purchaseUnit: AutomaticBillingUnitSchema.parse(productConfiguration.purchaseUnit),
            additionalProperties: {
                freightType,
                vehicleClass,
                loadingLocation,
                unloadingLocation,
                disposalPurpose,
                disposalCertificateNumber,
                weighingLocation,
            },
        },
        carrierOrganizationId,
    };
}
